<script setup lang="ts">
import { computed } from 'vue'
import { Button } from '@/components/ui'
import { track } from '@/lib/usageTracker'

/**
 * Shared pagination control. Supports two modes:
 *
 * - 'page' (default): standard prev / next / N-of-M with an optional
 *   per-page size select. Emits `update:page` and `update:pageSize`.
 * - 'load-more': a single Load more button that appends the next page.
 *   The consumer passes a sentinel `total` (loadedLimit + canLoadMore ? 1 : 0)
 *   so the component knows whether more is available without exposing the
 *   cursor to the public API. Emits `load-more`.
 *
 * The component intentionally owns prev/next click tracking via
 * `trackLabel + 'Prev'/'Next'/'LoadMore'` so the existing entity pages
 * keep their `btn:<Entity>PagePrev/Next` tracking contract without
 * having to wire it from each SFC.
 */
type Mode = 'page' | 'load-more'

const props = withDefaults(defineProps<{
  page: number
  pageSize: number
  total: number
  mode?: Mode
  pageSizeOptions?: number[]
  loading?: boolean
  label?: string
  trackLabel?: string
}>(), {
  mode: 'page',
  pageSizeOptions: () => [25, 50, 100, 200],
  loading: false,
  label: 'results',
  trackLabel: '',
})

const emit = defineEmits<{
  'update:page': [number]
  'update:pageSize': [number]
  'load-more': []
}>()

const totalPages = computed(() => {
  if (props.pageSize <= 0) return 1
  return Math.max(1, Math.ceil(props.total / props.pageSize))
})

const hasPrev = computed(() => props.page > 1)
const hasNext = computed(() => props.page < totalPages.value)

// In 'load-more' mode the consumer passes a sentinel `total` that is
// (loadedLimit + 1) when more is available and (loadedLimit) when not.
// We use the simple `total > page * pageSize` math that works for both.
const canLoadMore = computed(() => props.total > props.page * props.pageSize)

const startIndex = computed(() => {
  if (props.total === 0) return 0
  return (props.page - 1) * props.pageSize + 1
})

const endIndex = computed(() => {
  if (props.total === 0) return 0
  return Math.min(props.page * props.pageSize, props.total)
})

function emitPrev() {
  if (props.trackLabel) track(`${props.trackLabel}Prev`, 'click')
  emit('update:page', props.page - 1)
}

function emitNext() {
  if (props.trackLabel) track(`${props.trackLabel}Next`, 'click')
  emit('update:page', props.page + 1)
}

function emitLoadMore() {
  if (props.trackLabel) track(`${props.trackLabel}LoadMore`, 'click')
  emit('load-more')
}

function onPageSizeChange(event: Event) {
  const value = Number((event.target as HTMLSelectElement).value)
  if (props.trackLabel) track(`${props.trackLabel}PageSize`, 'click', { size: value })
  emit('update:pageSize', value)
  emit('update:page', 1)
}
</script>

<template>
  <div v-if="mode === 'page'" class="pagination">
    <div class="pagination-info">
      <template v-if="total === 0">
        Showing 0 {{ label }}
      </template>
      <template v-else-if="startIndex === endIndex">
        Showing {{ startIndex }} of {{ total }} {{ label }}
      </template>
      <template v-else>
        Showing {{ startIndex }}–{{ endIndex }} of {{ total }} {{ label }}
      </template>
    </div>

    <div class="pagination-controls">
      <Button
        variant="ghost"
        size="sm"
        :disabled="!hasPrev || loading"
        @click="emitPrev"
      >
        Previous
      </Button>
      <span class="pagination-page-info">
        Page {{ page }} of {{ totalPages }}
      </span>
      <Button
        variant="ghost"
        size="sm"
        :disabled="!hasNext || loading"
        @click="emitNext"
      >
        Next
      </Button>
    </div>

    <label class="pagination-size">
      <span class="pagination-size-label">Show</span>
      <select
        class="pagination-size-select"
        :value="pageSize"
        :disabled="loading"
        @change="onPageSizeChange"
      >
        <option v-for="opt in pageSizeOptions" :key="opt" :value="opt">
          {{ opt }}
        </option>
      </select>
      <span class="pagination-size-label">per page</span>
    </label>
  </div>

  <div v-else class="pagination pagination--load-more">
    <div class="pagination-info">
      Showing {{ total === 0 ? 0 : page * pageSize > total ? total : page * pageSize }} {{ label }}
    </div>
    <Button
      variant="secondary"
      size="sm"
      :disabled="!canLoadMore || loading"
      :loading="loading"
      @click="emitLoadMore"
    >
      Load more
    </Button>
  </div>
</template>

<style scoped>
.pagination {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  font-size: 0.75rem;
  color: var(--color-ink-soft);
  flex-wrap: wrap;
}

.pagination--load-more {
  justify-content: space-between;
}

.pagination-info {
  flex: 0 0 auto;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1 1 auto;
  justify-content: center;
}

.pagination-page-info {
  min-width: 6.5rem;
  text-align: center;
}

.pagination-size {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex: 0 0 auto;
  margin-left: auto;
}

.pagination-size-label {
  color: var(--color-ink-soft);
}

.pagination-size-select {
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  background: var(--color-surface);
  color: var(--color-ink);
}

.pagination-size-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .pagination {
    flex-direction: column;
    align-items: stretch;
    gap: 0.6rem;
  }
  .pagination-controls {
    justify-content: space-between;
  }
  .pagination-page-info {
    min-width: 0;
  }
  .pagination-size {
    margin-left: 0;
    justify-content: center;
  }
}
</style>
